const ELLIPSIS = "..." as const;
const ELLIPSIS_LENGTH = ELLIPSIS.length;
const DEFAULT_MAX_LENGTH = 40;

function getFileExtension(filename: string): { base: string; ext: string } {
  const lastDotIndex = filename.lastIndexOf(".");

  if (lastDotIndex <= 0) {
    return { base: filename, ext: "" };
  }

  return {
    base: filename.slice(0, lastDotIndex),
    ext: filename.slice(lastDotIndex)
  };
}

/**
 * Truncates a string in the middle, preserving the file extension when present.
 * @param str - The string to truncate
 * @param maxLength - Maximum character length (default: 40)
 */
export function truncateMiddle(str: string, maxLength = DEFAULT_MAX_LENGTH): string {
  if (str.length <= maxLength) return str;

  const { base, ext } = getFileExtension(str);
  const availableForBase = maxLength - ext.length - ELLIPSIS_LENGTH;

  if (availableForBase <= 0) {
    return str.slice(0, maxLength - ELLIPSIS_LENGTH) + ELLIPSIS;
  }

  if (base.length <= availableForBase) return str;

  const half = Math.floor(availableForBase / 2);

  return `${base.slice(0, half)}${ELLIPSIS}${base.slice(-half)}${ext}`;
}

/**
 * Measures the rendered width of text in pixels using the given font.
 * Safe for SSR (returns 0 when document is undefined).
 */
export function measureTextWidth(text: string, font: string): number {
  if (typeof document === "undefined") return 0;

  const span = document.createElement("span");
  span.style.cssText = `position:absolute;visibility:hidden;white-space:nowrap;font:${font}`;
  span.textContent = text;
  document.body.appendChild(span);

  try {
    return span.getBoundingClientRect().width;
  } finally {
    document.body.removeChild(span);
  }
}

/**
 * Truncates a string in the middle to fit within a given pixel width.
 * Uses binary search to find the optimal truncation length.
 */
export function truncateToFit(str: string, maxWidthPx: number, fontElement: HTMLElement): string {
  if (!str || maxWidthPx <= 0) return str;

  const font = window.getComputedStyle(fontElement).font;
  const fits = (text: string) => measureTextWidth(text, font) <= maxWidthPx;

  if (fits(str)) return str;

  let lo = 1;
  let hi = str.length;

  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2);
    if (fits(truncateMiddle(str, mid))) {
      lo = mid;
    } else {
      hi = mid - 1;
    }
  }

  return truncateMiddle(str, lo);
}
